// src/pages/admin/AdminDashboard.tsx

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '../../../api'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts'

// 출석현황
interface AttendanceBar {
  affiliation_type: string
  label: string
  attendance_count: number
}

// 프로그램 신청
interface ProgramApplication {
  application_id: number
  program_id: number
  program_name: string
  session_name: string | null
  applicant_login_id: string | null
  applicant_name: string | null
  phone: string | null
  affiliation_name: string | null
  status: string
  applied_at: string
}

// 상담 신청
interface CounselingApplication {
  application_id: number
  member_name: string | null
  login_id: string | null
  phone: string | null
  scheduled_date: string | null
  start_time: string | null
  end_time: string | null
  status: number
  applied_at: string
}

// 공간 예약
interface SpaceReservation {
  reservation_id: number
  space_name: string
  reserve_date: string
  start_time: string | null
  end_time: string | null
  label: string | null
  applicant_name: string | null
  phone: string | null
  status: number
}

// 금고 예약
interface LockerReservation {
  reservation_id: number
  locker_id: number
  locker_number: number
  member_id: number
  member_name: string | null
  phone: string | null
  start_date: string
  end_date: string
  status: number
  created_at: string
}

// 포인트 내역
interface PointHistory {
  id: number
  member_id: number
  member_name: string
  type: number
  description: string
  amount: number
  balance_after: number
  ticket_no: string | null
  is_confirmed: number
  confirmed_at: string | null
  created_at: string
}

const ATTENDANCE_COLORS: Record<string, string> = {
  elementary: '#887267',
  middle: '#f8b62c',
  high: '#f8b62c',
  out_of_school: '#e8e0dc',
  other: '#d1d5db',
}

const LOCKER_STATUS_LABEL: Record<number, string> = {
  0: '확인대기',
  1: '확인완료',
  2: '신청취소',
  3: '이용종료',
}

const STATUS_COLOR: Record<string, string> = {
  '신청': 'text-[#887267] bg-[#f5f2f0]',
  '예약완료': 'text-green-600 bg-green-50',
  '신청취소': 'text-red-400 bg-red-50',
  '확인대기': 'text-yellow-600 bg-yellow-50',
  '확인완료': 'text-green-600 bg-green-50',
  '이용종료': 'text-blue-600 bg-blue-50',
}

export default function AdminDashboard() {
  const navigate = useNavigate()

  const [attendanceRange, setAttendanceRange] = useState<'week' | 'month' | 'year'>('week')
  const [attendanceData, setAttendanceData] = useState<AttendanceBar[]>([])
  const [programApps, setProgramApps] = useState<ProgramApplication[]>([])
  const [counselApps, setCounselApps] = useState<CounselingApplication[]>([])
  const [spaceReservations, setSpaceReservations] = useState<SpaceReservation[]>([])
  const [lockerReservations, setLockerReservations] = useState<LockerReservation[]>([])
  const [pointHistories, setPointHistories] = useState<PointHistory[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchAll()
  }, [])

  useEffect(() => {
    fetchAttendance()
  }, [attendanceRange])

  const fetchAttendance = async () => {
    try {
      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/attendance/stats/school-type?range=${attendanceRange}`)
      )

      if (res.ok) {
        const data = await res.json()
        setAttendanceData(data.bars)
      }
    } catch {}
  }

  const fetchAll = async () => {
    setLoading(true)
    try {
      await Promise.all([
        fetchAttendance(),
        // 프로그램 신청 - 전체 프로그램 최신순
        fetchWithAuth(getApiUrl('/marubit/api/program/admin/applications?limit=5'))
          .then(r => r.ok ? r.json() : null)
          .then(d => d && setProgramApps(d.applications?.slice(0, 5) ?? [])),
        fetchWithAuth(getApiUrl('/marubit/api/counseling/admin/applications?limit=5'))
          .then(r => r.ok ? r.json() : null)
          .then(d => d && setCounselApps(d?.slice(0, 5) ?? [])),
        fetchWithAuth(getApiUrl('/marubit/api/space/admin/reservations?limit=5'))
          .then(r => r.ok ? r.json() : null)
          .then(d => d && setSpaceReservations(d.reservations?.slice(0, 5) ?? [])),
        fetchWithAuth(getApiUrl('/marubit/api/admin/locker/reservations?skip=0&limit=5'))
          .then(r => r.ok ? r.json() : null)
          .then(d => d && setLockerReservations(d.reservations?.slice(0, 5) ?? [])),
        fetchWithAuth(getApiUrl('/marubit/api/point/admin/histories?page=1&limit=5'))
          .then(r => r.ok ? r.json() : null)
          .then(d => d && setPointHistories(d.histories?.slice(0, 5) ?? [])),
      ])
    } catch {}
    setLoading(false)
  }

  const formatDate = (str: string | null) => {
    if (!str) return '-'
    return new Date(str).toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' })
  }

  const formatDateTime = (str: string) =>
    new Date(str).toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })

  const Card = ({ title, onArrow, children }: { title: string; onArrow?: () => void; children: React.ReactNode }) => (
    <div className="bg-white rounded-2xl f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)] overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <h2 className="text-[15px] font-bold text-[#222] m-0">{title}</h2>
        {onArrow && (
          <button onClick={onArrow} className="text-[#887267] bg-transparent border-none cursor-pointer text-[18px] hover:scale-110 transition-transform">→</button>
        )}
      </div>
      {children}
    </div>
  )

  const StatusBadge = ({ status }: { status: string }) => (
    <span className={`px-2 py-[2px] text-[11px] font-semibold rounded-full ${STATUS_COLOR[status] ?? 'text-gray-500 bg-gray-100'}`}>
      {status}
    </span>
  )

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center text-gray-300">
      불러오는 중...
    </div>
  )

  return (
    <div className="bg-[#f5f2f0] min-h-screen p-6 w-full">
      <div className="max-w-[1400px] mx-auto">

        {/* 헤더 */}
        <div className="mb-6">
          <h1 className="text-[24px] font-extrabold text-[#2a1f1a] mb-1">대시보드</h1>
          <p className="text-[13px] text-gray-400">{new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}</p>
        </div>

        {/* 하단 카드 그리드 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">

            {/* 출석현황 */}
            <Card title="출석현황">
                <div className="p-4">
                <div className="flex gap-2 mb-3">
                    {(['week', 'month', 'year'] as const).map(r => (
                    <button
                        key={r}
                        onClick={() => setAttendanceRange(r)}
                        className={[
                        'px-3 py-[5px] text-[12px] font-semibold rounded-lg border transition-colors cursor-pointer',
                        attendanceRange === r
                            ? 'bg-[#887267] text-white border-[#887267]'
                            : 'bg-white text-gray-500 border-gray-200 hover:border-[#887267] hover:text-[#887267]',
                        ].join(' ')}
                    >
                        {r === 'week' ? '1주일' : r === 'month' ? '1달' : '1년'}
                    </button>
                    ))}
                </div>
                <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={attendanceData} barSize={32}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="attendance_count" name="출석수" radius={[4, 4, 0, 0]}>
                        {attendanceData.map((entry, index) => (
                        <rect key={index} fill={ATTENDANCE_COLORS[entry.affiliation_type] ?? '#887267'} />
                        ))}
                    </Bar>
                    </BarChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-3 mt-2">
                    {attendanceData.map(item => (
                    <div key={item.affiliation_type} className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ATTENDANCE_COLORS[item.affiliation_type] ?? '#887267' }} />
                        <span className="text-[11px] text-gray-500">{item.label} {item.attendance_count}명</span>
                    </div>
                    ))}
                </div>
                </div>
            </Card>

          {/* 프로그램 신청 */}
          <Card title="프로그램 신청내역" onArrow={() => navigate('/admin/program')}>
            <table className="w-full text-center">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-center px-4 py-2 text-[12px] text-gray-400 font-medium">신청자</th>
                  <th className="text-center px-4 py-2 text-[12px] text-gray-400 font-medium">프로그램</th>
                  <th className="text-center px-4 py-2 text-[12px] text-gray-400 font-medium">상태</th>
                </tr>
              </thead>
              <tbody>
                {programApps.length === 0 ? (
                  <tr><td colSpan={3} className="text-center py-6 text-[13px] text-gray-300">내역이 없습니다.</td></tr>
                ) : programApps.map(app => (
                  <tr
                    key={app.application_id}
                    className="border-t border-gray-50 hover:bg-[#f5f2f0] cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/program/${app.program_id}/applications/${app.application_id}`)}
                  >
                    <td className="px-4 py-3 text-[13px] text-[#222]">{app.applicant_name ?? '-'}</td>
                    <td className="px-4 py-3 text-[13px] text-gray-500 truncate max-w-[160px]">{app.program_name}</td>
                    <td className="px-4 py-3"><StatusBadge status={app.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* 상담 신청 */}
          <Card title="상담신청 내역" onArrow={() => navigate('/admin/counsel')}>
            <table className="w-full text-center">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-center px-4 py-2 text-[12px] text-gray-400 font-medium">신청자</th>
                  <th className="text-center px-4 py-2 text-[12px] text-gray-400 font-medium">상담일</th>
                  <th className="text-center px-4 py-2 text-[12px] text-gray-400 font-medium">상태</th>
                </tr>
              </thead>
              <tbody>
                {counselApps.length === 0 ? (
                  <tr><td colSpan={3} className="text-center py-6 text-[13px] text-gray-300">내역이 없습니다.</td></tr>
                ) : counselApps.map(app => (
                  <tr
                    key={app.application_id}
                    className="border-t border-gray-50 hover:bg-[#f5f2f0] cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/counsel/${app.application_id}`)}
                  >
                    <td className="px-4 py-3 text-[13px] text-[#222]">{app.member_name ?? '-'}</td>
                    <td className="px-4 py-3 text-[13px] text-gray-500">{formatDate(app.scheduled_date)}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={app.status === 0 ? '확인대기' : app.status === 1 ? '확인완료' : '신청취소'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* 공간 예약 */}
          <Card title="공간예약 내역" onArrow={() => navigate('/admin/space')}>
            <table className="w-full text-center">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-center px-4 py-2 text-[12px] text-gray-400 font-medium">공간명</th>
                  <th className="text-center px-4 py-2 text-[12px] text-gray-400 font-medium">신청자</th>
                  <th className="text-center px-4 py-2 text-[12px] text-gray-400 font-medium">예약일</th>
                  <th className="text-center px-4 py-2 text-[12px] text-gray-400 font-medium">상태</th>
                </tr>
              </thead>
              <tbody>
                {spaceReservations.length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-6 text-[13px] text-gray-300">내역이 없습니다.</td></tr>
                ) : spaceReservations.map(r => (
                  <tr
                    key={r.reservation_id}
                    className="border-t border-gray-50 hover:bg-[#f5f2f0] cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/reservation/space/${r.reservation_id}`)}
                  >
                    <td className="px-4 py-3 text-[13px] text-[#222]">{r.space_name}</td>
                    <td className="px-4 py-3 text-[13px] text-gray-500">{r.applicant_name ?? '-'}</td>
                    <td className="px-4 py-3 text-[13px] text-gray-500">{formatDate(r.reserve_date)}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={r.status === 0 ? '확인대기' : r.status === 1 ? '확인완료' : '신청취소'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* 금고 예약 */}
          <Card title="금고예약 내역" onArrow={() => navigate('/admin/locker')}>
            <table className="w-full text-center">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-center px-4 py-2 text-[12px] text-gray-400 font-medium">금고번호</th>
                  <th className="text-center px-4 py-2 text-[12px] text-gray-400 font-medium">신청자</th>
                  <th className="text-center px-4 py-2 text-[12px] text-gray-400 font-medium">이용 시작일</th>
                  <th className="text-center px-4 py-2 text-[12px] text-gray-400 font-medium">상태</th>
                </tr>
              </thead>
              <tbody>
                {lockerReservations.length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-6 text-[13px] text-gray-300">내역이 없습니다.</td></tr>
                ) : lockerReservations.map(r => (
                  <tr
                    key={r.reservation_id}
                    className="border-t border-gray-50 hover:bg-[#f5f2f0] cursor-pointer transition-colors"
                    onClick={() => navigate('/admin/locker')}
                  >
                    <td className="px-4 py-3 text-[13px] text-[#222]">{r.locker_number}번</td>
                    <td className="px-4 py-3 text-[13px] text-gray-500">{r.member_name ?? '-'}</td>
                    <td className="px-4 py-3 text-[13px] text-gray-500">{formatDate(r.start_date)}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={LOCKER_STATUS_LABEL[r.status] ?? '-'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* 포인트 내역 */}
          <Card title="포인트 사용내역" onArrow={() => navigate('/admin/point')}>
            <table className="w-full text-center">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-center px-4 py-2 text-[12px] text-gray-400 font-medium">사용자</th>
                  <th className="text-center px-4 py-2 text-[12px] text-gray-400 font-medium">내용</th>
                  <th className="text-center px-4 py-2 text-[12px] text-gray-400 font-medium">포인트</th>
                  <th className="text-center px-4 py-2 text-[12px] text-gray-400 font-medium">확인</th>
                </tr>
              </thead>
              <tbody>
                {pointHistories.length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-6 text-[13px] text-gray-300">내역이 없습니다.</td></tr>
                ) : pointHistories.map(p => (
                  <tr
                    key={p.id}
                    className="border-t border-gray-50 hover:bg-[#f5f2f0] cursor-pointer transition-colors"
                    onClick={() => navigate('/admin/point')}
                  >
                    <td className="px-4 py-3 text-[13px] text-[#222]">{p.member_name}</td>
                    <td className="px-4 py-3 text-[13px] text-gray-500 max-w-[120px] truncate">{p.description}</td>
                    <td className={`px-4 py-3 text-[13px] font-semibold ${p.type === 1 ? 'text-[#887267]' : 'text-red-400'}`}>
                      {p.type === 1 ? '+' : '-'}{p.amount.toLocaleString()}P
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={p.is_confirmed === 1 ? '확인완료' : '확인대기'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

        </div>
      </div>
    </div>
  )
}